import {
  type MutableRefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type ScrollAndOverflowInfoType = {
  elementCanScrollLeft: boolean;
  elementCanScrollRight: boolean;
  elementIsOverflowingHorizontally: boolean;
  elementIsOverflowingVertically: boolean;
  isScrollbarAtTop: boolean;
  isScrollbarAtBottom: boolean;
  isScrollbarAtLeft: boolean;
  isScrollbarAtRight: boolean;
};

export const useGetScrollAndOverflowInfo = (): [
  MutableRefObject<HTMLDivElement | null>,
  ScrollAndOverflowInfoType,
] => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scrollAndOverflowInfo, setScrollAndOverflowInfo] =
    useState<ScrollAndOverflowInfoType>({
      elementCanScrollLeft: false,
      elementCanScrollRight: false,
      elementIsOverflowingHorizontally: false,
      elementIsOverflowingVertically: false,
      isScrollbarAtTop: false,
      isScrollbarAtBottom: false,
      isScrollbarAtLeft: false,
      isScrollbarAtRight: false,
    });

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const checkScrollInfo = () => {
      const hasHorizontal = element.scrollWidth > element.clientWidth;
      const hasVertical = element.scrollHeight > element.clientHeight;

      const scrollEdgeThreshold = 1;
      const isAtTop = element.scrollTop <= scrollEdgeThreshold;
      const isAtBottom =
        Math.ceil(element.scrollTop + element.clientHeight) >=
        element.scrollHeight - scrollEdgeThreshold;
      const isAtLeft = element.scrollLeft <= scrollEdgeThreshold;
      const isAtRight =
        Math.ceil(element.scrollLeft + element.clientWidth) >=
        element.scrollWidth - scrollEdgeThreshold;

      setScrollAndOverflowInfo({
        elementCanScrollLeft: hasHorizontal && !isAtLeft,
        elementCanScrollRight: hasHorizontal && !isAtRight,
        elementIsOverflowingHorizontally: hasHorizontal,
        elementIsOverflowingVertically: hasVertical,
        isScrollbarAtTop: hasVertical ? isAtTop : true,
        isScrollbarAtBottom: hasVertical ? isAtBottom : true,
        isScrollbarAtLeft: hasHorizontal ? isAtLeft : true,
        isScrollbarAtRight: hasHorizontal ? isAtRight : true,
      });
    };

    const resizeObserver = new ResizeObserver(checkScrollInfo);
    resizeObserver.observe(element);
    element.addEventListener("scroll", checkScrollInfo, { passive: true });

    checkScrollInfo();

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener("scroll", checkScrollInfo);
    };
  }, [ref]);

  return [ref, scrollAndOverflowInfo] as const;
};
